import React from 'react';
import { ArrowRight, CheckCircle, Shield } from 'lucide-react';
import { objectives } from '../mockData';

const PHOTO_URL = "https://customer-assets.emergentagent.com/job_02301d40-e1b7-4c3b-a98b-eccef76882c0/artifacts/mjewsncf_Photos%20sans%20fond.png";
// Clean olive tree banner (without baked-in marketing text)
const BG_URL = "https://customer-assets.emergentagent.com/job_bf684596-217b-4b7d-b6a0-45cda0e5d97c/artifacts/in905z2l_bandeau%20linkedin%20sans%20%C3%A9criture.jpg";

const Hero = () => {
  return (
    <section id="hero" data-testid="hero-section" style={{
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      background: 'var(--bg-dark)'
    }}>
      {/* Clean olive-tree banner background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `url(${BG_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center right',
        zIndex: 0,
        filter: 'brightness(0.5) saturate(0.7)',
        opacity: 0.78
      }} />
      {/* Brown gradient overlay - left fully opaque (hide bg text), right keeps olive silhouette */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to right, var(--bg-dark) 0%, var(--bg-dark) 35%, rgba(58, 46, 15, 0.88) 55%, rgba(58, 46, 15, 0.55) 100%)',
        zIndex: 1
      }} />
      {/* Gold accent top line */}
      <div style={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: '3px',
        background: 'linear-gradient(90deg, var(--primary-gold), transparent 60%)', zIndex: 3
      }} />

      <div className="container" style={{ position: 'relative', zIndex: 2, paddingTop: '5rem', paddingBottom: '3rem' }}>
        <div className="hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '3rem', alignItems: 'center' }}>

          {/* Left - Content */}
          <div className="animate-fade-in-up">
            <span className="section-label-light" style={{ marginBottom: '1rem' }}>ActiV Patrimoine</span>

            <h1 className="heading-hero" style={{ color: 'white', marginBottom: '1.1rem', fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', lineHeight: 1.1 }}>
              Tout mettre en œuvre<br />
              <span style={{ color: 'var(--primary-gold)' }}>pour atteindre vos objectifs.</span>
            </h1>

            <p style={{
              fontSize: '0.98rem',
              color: 'rgba(255,255,255,0.78)',
              lineHeight: '1.7',
              marginBottom: '0.85rem',
              maxWidth: '580px'
            }}>
              <span style={{ fontWeight: '500', color: 'rgba(255,255,255,0.95)' }}>Vous vous posez des questions&nbsp;?</span> <span style={{ color: 'rgba(255,255,255,0.6)' }}>…</span> <span style={{ fontWeight: '600', color: 'rgba(255,255,255,0.95)' }}>Mais vous ne savez pas par où commencer.</span>
            </p>
            <p style={{
              fontWeight: '400',
              fontSize: '0.98rem',
              color: 'var(--primary-gold)',
              marginBottom: '1rem',
              maxWidth: '580px',
              lineHeight: 1.65
            }}>
              Je vous accompagne pour structurer, optimiser et faire évoluer votre patrimoine simplement.
            </p>

            {/* Highlighted offer badge */}
            <div data-testid="hero-offer-badge" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.55rem',
              padding: '0.55rem 1.1rem',
              border: '1.5px solid var(--primary-gold)',
              borderRadius: '2rem',
              background: 'rgba(172, 153, 105, 0.14)',
              marginBottom: '1.5rem',
              boxShadow: '0 0 24px rgba(172, 153, 105, 0.18)'
            }}>
              <span style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: 'var(--primary-gold)',
                boxShadow: '0 0 10px var(--primary-gold)'
              }} />
              <span style={{
                color: 'var(--primary-gold)',
                fontWeight: '700',
                fontSize: '0.78rem',
                letterSpacing: '0.14em',
                textTransform: 'uppercase'
              }}>
                Profitez d'un bilan patrimonial offert
              </span>
            </div>

            {/* Benefits - compact */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem 1.5rem', marginBottom: '1.75rem' }}>
              {[
                'Bilan patrimonial sans engagement',
                'Solutions sur-mesure haut de gamme',
                'Accessible à tous'
              ].map((text, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <CheckCircle size={15} style={{ color: 'var(--primary-gold)', flexShrink: 0 }} />
                  <span style={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.85rem' }}>{text}</span>
                </div>
              ))}
            </div>

            {/* CTAs removed from hero - relocated below "Ma démarche" cards */}
          </div>

          {/* Right - Photo Card - light brown translucent (more compact) */}
          <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div style={{
              background: 'linear-gradient(160deg, rgba(58, 46, 15, 0.62) 0%, rgba(74, 58, 28, 0.55) 100%)',
              border: '1px solid rgba(172, 153, 105, 0.55)',
              borderRadius: '1rem',
              padding: '1.25rem 1.1rem 1.4rem',
              textAlign: 'center',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)'
            }}>
              <div style={{
                width: '240px', height: '240px',
                borderRadius: '50%', overflow: 'hidden',
                margin: '0 auto 1rem',
                border: '4px solid var(--primary-gold)',
                boxShadow: '0 8px 32px rgba(58, 46, 15, 0.3)',
                background: '#fff'
              }}>
                <img
                  src={PHOTO_URL}
                  alt="Arthur Vacogne - Conseiller en gestion de patrimoine"
                  loading="eager"
                  decoding="async"
                  style={{
                    width: '100%', height: '100%', objectFit: 'cover',
                    imageRendering: 'auto',
                    transform: 'scale(1.02)'
                  }}
                />
              </div>
              <h3 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '1.35rem', fontWeight: '600',
                color: 'white', marginBottom: '0.35rem',
                letterSpacing: '-0.01em'
              }}>Arthur Vacogne</h3>
              <p style={{
                fontSize: '0.75rem', lineHeight: '1.5',
                color: 'rgba(255,255,255,0.85)', marginBottom: '0.8rem'
              }}>
                Conseiller en Investissements Financiers (CIF)<br />
                Membre CNCIF - ORIAS<br />
                <strong style={{ color: 'white' }}>Basé à Annecy · Intervention France entière</strong>
              </p>
              <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.4rem',
                padding: '0.4rem 0.85rem',
                border: '1px solid var(--primary-gold)',
                borderRadius: '2rem',
                background: 'rgba(172, 153, 105, 0.22)',
                width: 'fit-content', margin: '0 auto'
              }}>
                <Shield size={13} style={{ color: 'var(--primary-gold)' }} />
                <span style={{ fontSize: '0.6rem', color: 'white', fontWeight: '700', letterSpacing: '0.1em' }}>
                  CERTIFIÉ AMF
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Objectives */}
        <div style={{ marginTop: '2.5rem', textAlign: 'center' }}>
          <p style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.1rem', fontWeight: '400',
            color: 'var(--primary-gold)', marginBottom: '0.75rem'
          }}>Je vous accompagne sur :</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', justifyContent: 'center' }}>
            {objectives.slice(0, 10).map((objective, index) => (
              <span key={index} data-testid={`objective-${index}`} style={{
                padding: '0.35rem 0.85rem',
                fontSize: '0.78rem', fontWeight: '500',
                color: 'rgba(255,255,255,0.82)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: '2rem',
                background: 'rgba(255,255,255,0.06)',
                transition: 'all 0.3s ease'
              }}>
                {objective}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
